import { assertNever, notNullish } from "@de-xima/xima-common-js-lang";
import { findWidgetForId } from "../../Util.ts";
import { AbstractComponentHandler } from "../AbstractComponentHandler.ts";
import type { IComponentHandlerCallbacks, IInsertData } from "../../Header.ts";

const SELECTOR_QUILL_EDITOR_WRAPPER = ".ui-texteditor";

/**
 * Handler for the Quill editor JSF wrapper.
 *
 * JavaScript library can be found here:
 * https://quilljs.com/
 *
 */
export class QuillEditorHandler extends AbstractComponentHandler {
  override accepts($element: JQuery): boolean {
    return this._getWrapper($element).length === 1;
  }

  override focus($element: JQuery): void {
    const widget = this._getWidget($element);
    widget?.editor.focus();
  }

  override getInputElement($element: JQuery): JQuery {
    const input = this._getWidget($element);
    return input?.jq ?? jQuery();
  }

  override initialize($element: JQuery, callbacks: IComponentHandlerCallbacks): boolean {
    return this._initialize($element, callbacks);
  }

  override insertText($element: JQuery, data: IInsertData): void {
    const widget = this._getWidget($element);
    const editor = widget?.editor;
    if (notNullish(editor)) {
      const selection = editor.getSelection(true);
      if (notNullish(selection)) {
        editor.deleteText(selection.index, selection.length);
        switch (data.insertHint) {
          case "text":
            editor.insertText(selection.index, data.content);
            break;
          case "html":
            editor.clipboard.dangerouslyPasteHTML(selection.index, data.content);
            break;
          case "link":
            editor.insertText(selection.index, data.content);
            editor.formatText(selection.index, data.content.length, "link", data.content);
            break;
          default:
            assertNever(data.insertHint);
        }
      }
    }
  }

  /**
   * @param $element Input component.
   * @returns Monaco editor widget instance.
   */
  private _getWidget($element: JQuery): PrimeFaces.widget.TextEditor | undefined {
    const $wrapper = this._getWrapper($element);
    const id = $wrapper.attr("id") ?? "";
    return findWidgetForId(id, PrimeFaces.widget.TextEditor);
  }

  /**
   * @param $element Input component.
   * @returns The wrapping div of the editor.
   */
  private _getWrapper($element: JQuery): JQuery {
    if ($element.is(SELECTOR_QUILL_EDITOR_WRAPPER)) {
      return $element;
    }
    return $element.find(SELECTOR_QUILL_EDITOR_WRAPPER);
  }

  /**
   * Same as the initialize method, but called once the Quill is ready and initialized.
   * @param $element Input component.
   * @param callbacks Callbacks we can invoke.
   */
  private _initialize($element: JQuery, callbacks: IComponentHandlerCallbacks): boolean {
    const widget = this._getWidget($element);
    if (widget === undefined) {
      return false;
    }
    const editor = widget.editor;
    if (editor !== undefined) {
      editor.on("selection-change", (range) => {
        if (notNullish(range)) {
          callbacks.onFocus();
        }
      });
      editor.keyboard.addBinding({ key: " ", shortKey: true }, () => {
        callbacks.onPlaceholderRequested();
      });
    }
    return true;
  }
}
